import {Avatar} from 'platform/components';
import {
  Box,
  Center,
  Hide,
  Show,
  HStack,
  VStack,
  Text,
  Heading,
  Space,
  Clickable,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {css} from 'styled-components';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useReadCodeQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {useGetInterestV2Query} from '@dms/api/finance';
import {InterestActivityResponseBody, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  ActivityDate,
  ActivitySidebarLink,
  getActivityIconColor,
  transformActivityItem,
} from '@dms/features/activity-panel';
import {openEditTaskFormDialog} from '@dms/features/tasks';
import {getNaturalPersonFullName, useCanOpenTelephonyCall} from '@dms/shared';

import {DOT_CHARACTER, parseDate} from 'shared/web';

interface ActivityItemProps {
  item: InterestActivityResponseBody;
  showLineToNextItem: boolean;
  interestId: string;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function ActivityItem(props: ActivityItemProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: interestData} = useGetInterestV2Query(
    {id: props.interestId},
    {
      skip:
        props.item.activityType !== 'INTEREST_OPPORTUNITY_LOST' &&
        props.item.activityType !== 'INTEREST_CREATED' &&
        props.item.activityType !== 'INTEREST_CLOSED',
    }
  );

  const {data: sourceCodeData} = useReadCodeQuery(
    {codelistId: 'interest_source', codeId: interestData?.sourceCodeId ?? ''},
    {skip: !interestData?.sourceCodeId}
  );

  const {data: closeReasonData} = useReadCodeQuery(
    {codelistId: 'unsuccessful_interest_reason', codeId: interestData?.reasonCloseCodeId ?? ''},
    {skip: isNil(interestData?.reasonCloseCodeId)}
  );

  const {data: userData} = useGetUserQuery(
    {id: props.item.userId ?? ''},
    {skip: !props.item.userId || isNotNilOrEmpty(props.item.userName)}
  );

  const {data: taskAssigneeData} = useGetUserQuery(
    {id: props.item.relatedTask?.assignedTo ?? ''},
    {
      skip:
        !props.item.relatedTask &&
        props.item.activityType !== 'TASK_FINISHED' &&
        props.item.activityType !== 'TASK_CREATED',
    }
  );

  const {data: vehicleData} = useGetVehicleQuery(
    {
      vehicleId: props.item.relatedTestDrive?.vehicleId ?? '',
    },
    {skip: isNilOrEmpty(props.item.relatedTestDrive?.vehicleId)}
  );

  const handleOpenEditTaskFormDialog = (id: string) => {
    openEditTaskFormDialog({
      id,
      'data-testid': 'activity-panel-task-edit',
    });
  };

  const canOpenCall = useCanOpenTelephonyCall();
  const transformedItem = transformActivityItem(
    props.item,
    interestData,
    {
      canOpenCall,
      source: sourceCodeData,
      closeReason: closeReasonData,
      userName:
        taskAssigneeData?.firstName && taskAssigneeData?.lastName
          ? `${taskAssigneeData?.firstName} ${taskAssigneeData?.lastName}`
          : undefined,
      vehicleTitle: vehicleData?.title,
    },
    EntityResourceIds.interest,
    handleOpenEditTaskFormDialog
  );

  const hasUserName = isNotNilOrEmpty(props.item.userName) || isNotNil(userData);
  const hasRelatedTestDrive = Boolean(transformedItem?.relatedTestDrive);

  if (!transformedItem) {
    return null;
  }

  const metadataItems = [
    transformedItem.relatedTestDrive?.vehicleTitle,
    transformedItem.relatedTestDrive?.scheduledAt &&
      formatDateTime('dateTimeShort', parseDate(transformedItem.relatedTestDrive.scheduledAt)),
    transformedItem.interestType,
    transformedItem?.userName ?? getNaturalPersonFullName(userData),
  ].filter(Boolean);

  return (
    <HStack spacing={2}>
      <VStack>
        <Avatar
          color={transformedItem.color}
          icon={transformedItem.icon}
          variant="square"
          iconColor={getActivityIconColor(transformedItem.color)}
        />
        <Hide when={props.showLineToNextItem}>
          <Center width="100%" height="100%">
            <Box height="100%" backgroundColor="general.separator" width="0.125rem" />
          </Center>
        </Hide>
      </VStack>
      <VStack width="100%">
        <Box paddingBottom={4}>
          <VStack spacing={1}>
            <Box paddingBottom={0}>
              <HStack width="100%">
                <VStack width="100%" spacing={1}>
                  <Clickable
                    onClick={transformedItem.onClick}
                    isDisabled={!transformedItem.isEditable}
                  >
                    <Heading size={5}>{transformedItem?.title}</Heading>
                  </Clickable>
                  <Show when={transformedItem?.interestType || hasUserName || hasRelatedTestDrive}>
                    <Text color="tertiary" size="xSmall">
                      <HStack align="center" wrap>
                        {metadataItems.map((item, index) => (
                          <span
                            key={`${item}-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
                            css={css`
                              display: inline-flex;
                              align-items: center;
                              white-space: nowrap;
                            `}
                          >
                            {item}
                            <Show when={index < metadataItems.length - 1}>
                              <>
                                <Space horizontal={1} />
                                {DOT_CHARACTER}
                                <Space horizontal={1} />
                              </>
                            </Show>
                          </span>
                        ))}
                      </HStack>
                    </Text>
                  </Show>
                  <Show when={isNotNilOrEmpty(transformedItem?.description)}>
                    <Box maxWidth={62}>
                      <Text size="xSmall" color="secondary" noWrap>
                        {transformedItem?.description}
                      </Text>
                    </Box>
                  </Show>
                  <Show when={isNotNilOrEmpty(transformedItem?.links)}>
                    <HStack>
                      {transformedItem?.links?.map((link) => (
                        <ActivitySidebarLink key={link.label} {...link} />
                      ))}
                    </HStack>
                  </Show>
                </VStack>
                <ActivityDate timestamp={props.item.timestamp} />
              </HStack>
            </Box>
          </VStack>
        </Box>
      </VStack>
    </HStack>
  );
}
